import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { customFetcher } from '../useFetch';
import { ITask, ITaskFullObject } from '../../Interfaces/ITask';
import { ITaskType } from '../../Interfaces/ITaskType';
import { ITaskPriority } from '../../Interfaces/ITaskPriority';

export const useGetTasks = () => {
  const {
    data: tasks,
    isLoading: isLoadingTasks,
    refetch: refetechTasks,
  } = useQuery<ITaskFullObject[]>({
    queryKey: ['task'],
    queryFn: () => customFetcher({ endpoint: '/task' }),
  });

  return { tasks, isLoadingTasks, refetechTasks };
};

export const useGetTaskOwn = () => {
  const {
    data: tasks,
    isLoading: isLoadingTasks,
    refetch: refetechTasks,
  } = useQuery<ITask[]>({
    queryKey: ['task/ownTasks'],
    queryFn: () => customFetcher({ endpoint: '/task/ownTasks' }),
    refetchInterval: 5 * 60 * 1000, // alle 5 Minuten
    refetchIntervalInBackground: true, // auch wenn Tab nicht aktiv
  });

  return { tasks, isLoadingTasks, refetechTasks };
};

export const useGetTaskFullObject = (idTask?: number) => {
  const {
    data: taskFullObject,
    isLoading: isLoadingTaskFullObject,
    refetch: refetchTaskFullObject,
  } = useQuery<ITaskFullObject>({
    refetchOnWindowFocus: false,
    queryKey: ['task/fullObject', idTask],
    queryFn: () =>
      customFetcher({
        endpoint: `/task/fullObject/${idTask}`,
      }),
    enabled: !!idTask,
  });

  return {
    taskFullObject,
    isLoadingTaskFullObject,
    refetchTaskFullObject,
  };
};

export const useGetTaskType = () => {
  const {
    data: taskTypes,
    isLoading: isLoadingTaskTypes,
    refetch: refetechTaskTypes,
  } = useQuery<ITaskType[]>({
    refetchOnWindowFocus: false,
    queryKey: ['tasktype'],
    queryFn: () => customFetcher({ endpoint: '/tasktype' }),
  });

  return { taskTypes, isLoadingTaskTypes, refetechTaskTypes };
};

export const useGetTaskPriority = () => {
  const {
    data: taskPriorities,
    isLoading: isLoadingTaskPriorities,
    refetch: refetechTaskPriorities,
  } = useQuery<ITaskPriority[]>({
    refetchOnWindowFocus: false,
    queryKey: ['taskpriority'],
    queryFn: () => customFetcher({ endpoint: '/taskpriority' }),
  });

  return { taskPriorities, isLoadingTaskPriorities, refetechTaskPriorities };
};

export const useWriteTaskFullObject = () => {
  const queryClient = useQueryClient();
  const {
    mutateAsync: writeTaskFullObject,
    isPending: isPendingWriteTaskFullObject,
  } = useMutation({
    mutationFn: (uploadObject: ITaskFullObject) =>
      customFetcher({
        endpoint: '/task/fullObject',
        body: uploadObject,
        method: uploadObject.idTask < 0 ? 'POST' : 'PUT',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task'] });
      queryClient.invalidateQueries({ queryKey: ['task/ownTasks'] });
      // Fullobject-Cache leeren: enthält die (evtl. geänderten) Dokumente.
      // removeQueries statt invalidateQueries -> beim erneuten Öffnen frisch laden.
      queryClient.removeQueries({ queryKey: ['task/fullObject'] });
    },
  });

  return {
    writeTaskFullObject,
    isPendingWriteTaskFullObject,
  };
};

export const useDeleteTask = () => {
  const { mutateAsync: deleteTask, isPending: isPendingDeleteTask } =
    useMutation({
      mutationFn: (idTask: number) =>
        customFetcher({
          endpoint: `/task/${idTask}`,
          method: 'DELETE',
        }),
    });

  return { deleteTask, isPendingDeleteTask };
};
